import { Router } from "express";
import { z } from "zod";
import { and, asc, count, eq } from "drizzle-orm";
import { db } from "../../db/index";
import { teams, designations } from "../../db/schema/hr";
import { employeeDetails } from "../../db/schema/employee";
import { requireAuth, requirePermission, SCOPE } from "../../rbac/index";
import type { MySqlTable, AnyMySqlColumn } from "drizzle-orm/mysql-core";

/**
 * Org entities — Departments (teams) and Designations share an identical shape: a name + parent_id
 * tree with a headcount from employee_details. Ported from V2 DepartmentController L19–163 (list with
 * headcount/child-count/KPIs L33–67, store L90–111, update+self-parent-guard L117–143, delete with
 * child re-parent L149–163); Designations has no V2 controller → modelled on Departments (legacy
 * DesignationController). One factory builds both. Company-scoped; writes gate on ADD/EDIT/DELETE=ALL.
 */
type EntityCfg = {
  table: MySqlTable & { id: AnyMySqlColumn; companyId: AnyMySqlColumn; parentId: AnyMySqlColumn };
  nameCol: AnyMySqlColumn;
  headcountCol: AnyMySqlColumn; // employee_details.department_id | designation_id
  perms: { view: string; add: string; edit: string; del: string };
};

const CFG: Record<"departments" | "designations", EntityCfg> = {
  departments: {
    table: teams as never,
    nameCol: teams.teamName,
    headcountCol: employeeDetails.departmentId,
    perms: { view: "view_department", add: "add_department", edit: "edit_department", del: "delete_department" },
  },
  designations: {
    table: designations as never,
    nameCol: designations.name,
    headcountCol: employeeDetails.designationId,
    perms: { view: "view_designation", add: "add_designation", edit: "edit_designation", del: "delete_designation" },
  },
};

const bodySchema = z.object({
  name: z.string().trim().min(1).max(191),
  parent_id: z.coerce.number().int().positive().nullable().optional(),
});

export function orgEntityRoutes(router: Router, key: "departments" | "designations") {
  const cfg = CFG[key];
  const T = cfg.table;
  const requireAll = (perm: string) => (req: import("express").Request, res: import("express").Response, next: import("express").NextFunction) =>
    req.scope === SCOPE.ALL ? next() : res.status(403).json({ error: "Forbidden", permission: perm });

  /** GET — flat list with headcount + child-count, KPIs, parent options (legacy L33–67). */
  router.get(`/${key}`, requirePermission(cfg.perms.view), async (req, res) => {
    const companyId = req.auth!.companyId;
    const rows = await db
      .select({ id: T.id, name: cfg.nameCol, parentId: T.parentId })
      .from(T)
      .where(eq(T.companyId, companyId))
      .orderBy(asc(cfg.nameCol));
    // Coerce the generically-typed columns to concrete types once.
    const items = rows.map((r) => ({
      id: Number(r.id),
      name: String(r.name),
      parentId: r.parentId == null ? null : Number(r.parentId),
    }));
    const heads = await db
      .select({ pid: cfg.headcountCol, n: count() })
      .from(employeeDetails)
      .where(eq(employeeDetails.companyId, companyId))
      .groupBy(cfg.headcountCol);
    const headBy: Record<number, number> = {};
    for (const h of heads) if (h.pid != null) headBy[Number(h.pid)] = Number(h.n);
    const nameById = new Map(items.map((r) => [r.id, r.name]));
    const childCount: Record<number, number> = {};
    for (const r of items) if (r.parentId != null) childCount[r.parentId] = (childCount[r.parentId] ?? 0) + 1;

    const q = (req.query.search as string | undefined)?.toLowerCase().trim();
    let list = items.map((r) => ({
      id: r.id,
      name: r.name,
      parentId: r.parentId,
      parentName: r.parentId != null ? (nameById.get(r.parentId) ?? null) : null,
      headcount: headBy[r.id] ?? 0,
      childCount: childCount[r.id] ?? 0,
    }));
    if (q) list = list.filter((d) => d.name.toLowerCase().includes(q));

    res.json({
      rows: list,
      kpis: {
        total: list.length,
        topLevel: list.filter((d) => d.parentId == null).length,
        withKids: list.filter((d) => d.childCount > 0).length,
        headcount: list.reduce((s, d) => s + d.headcount, 0),
      },
      parentOptions: items.map((r) => ({ id: r.id, name: r.name })),
    });
  });

  /** POST — create (gate ADD=ALL, legacy L90–111). */
  router.post(`/${key}`, requirePermission(cfg.perms.add), requireAll(cfg.perms.add), async (req, res) => {
    const parsed = bodySchema.safeParse(req.body);
    if (!parsed.success) return res.status(422).json({ error: "Invalid payload", details: parsed.error.flatten() });
    const vals: Record<string, unknown> = { companyId: req.auth!.companyId, parentId: parsed.data.parent_id ?? null };
    vals[(cfg.nameCol as unknown as { name: string }).name === "team_name" ? "teamName" : "name"] = parsed.data.name;
    const [r] = await db.insert(T).values(vals as never).$returningId();
    res.status(201).json({ id: (r as { id: number }).id, message: "Created" });
  });

  /** PUT — update; block self-as-parent (gate EDIT=ALL, legacy L117–143). */
  router.put(`/${key}/:id`, requirePermission(cfg.perms.edit), requireAll(cfg.perms.edit), async (req, res) => {
    const id = Number(req.params.id);
    const parsed = bodySchema.safeParse(req.body);
    if (!parsed.success) return res.status(422).json({ error: "Invalid payload", details: parsed.error.flatten() });
    if (parsed.data.parent_id === id) return res.status(422).json({ error: "An item can't be its own parent" });
    const [exists] = await db.select({ id: T.id }).from(T).where(and(eq(T.id, id), eq(T.companyId, req.auth!.companyId))).limit(1);
    if (!exists) return res.status(404).json({ error: "Not found" });
    const set: Record<string, unknown> = { parentId: parsed.data.parent_id ?? null };
    set[(cfg.nameCol as unknown as { name: string }).name === "team_name" ? "teamName" : "name"] = parsed.data.name;
    await db.update(T).set(set as never).where(eq(T.id, id));
    res.json({ id, message: "Updated" });
  });

  /** DELETE — re-parent children to this item's parent, then delete (gate DELETE=ALL, legacy L149–163). */
  router.delete(`/${key}/:id`, requirePermission(cfg.perms.del), requireAll(cfg.perms.del), async (req, res) => {
    const id = Number(req.params.id);
    const [row] = await db.select({ id: T.id, parentId: T.parentId }).from(T).where(and(eq(T.id, id), eq(T.companyId, req.auth!.companyId))).limit(1);
    if (!row) return res.status(404).json({ error: "Not found" });
    await db.transaction(async (tx) => {
      await tx.update(T).set({ parentId: (row.parentId as number | null) ?? null } as never).where(eq(T.parentId, id));
      await tx.delete(T).where(eq(T.id, id));
    });
    res.json({ id, message: "Deleted" });
  });
}

export const orgRouter = Router();
orgRouter.use(requireAuth);
orgEntityRoutes(orgRouter, "departments");
orgEntityRoutes(orgRouter, "designations");
